'use client';

import React, { useState, useEffect } from 'react';
import { setLocalStorageItem, getLocalStorageItem } from '../lib/util';

const BANNER_KEY = 'litty_accountability_banner_shown';

interface AccountabilityBannerProps {
    className?: string;
    title?: string;
    description?: string;
    bodyText?: string;
}

const AccountabilityBanner: React.FC<AccountabilityBannerProps> = ({
    className,
    title = 'Litty holds venues accountable',
    description = 'We ensure your satisfaction before releasing payments to venues',
    bodyText = "We won't release funds for bookings until we've confirmed you're satisfied with your experience. Your enjoyment is our priority, and we're committed to making every Litty booking exceptional."
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        const hasBeenShown = getLocalStorageItem(BANNER_KEY) === 'true';
        if (!hasBeenShown) {
            setIsOpen(true);
        }
    }, []);

    const handleDismiss = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setLocalStorageItem(BANNER_KEY, 'true');
        }, 300);
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'
                    }`}
                onClick={handleDismiss}
            />

            {/* Modal */}
            <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
                }`}>
                <div
                    className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-300"
                    style={{
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header Section */}
                    <div className="relative px-8 pt-10 pb-8 bg-gradient-to-br from-[#1C1C34] to-[#2A2A4A] text-center">
                        {/* Subtle pattern overlay */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rotate-45 transform scale-150"></div>
                        </div>

                        {/* Shield Icon */}
                        <div className="relative mb-6 flex justify-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#C0C0DD] to-[#AEAEAE] rounded-2xl flex items-center justify-center shadow-lg">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-[#1C1C34]">
                                    <path d="M12 2L3 7V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V7L12 2ZM10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z" fill="currentColor" />
                                </svg>
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl font-semibold text-white mb-3 leading-tight" style={{ fontFamily: 'var(--font-h1)' }}>
                            {title}
                        </h1>

                        {/* Description */}
                        <p className="text-white/80 text-base leading-relaxed" style={{ fontFamily: 'var(--font-paragraph)' }}>
                            {description}
                        </p>
                    </div>

                    {/* Body Section */}
                    <div className="px-8 py-8">
                        <p className="text-gray-600 text-sm leading-relaxed mb-8 text-center" style={{ fontFamily: 'var(--font-paragraph)' }}>
                            {bodyText}
                        </p>

                        {/* Action Button */}
                        <button
                            onClick={handleDismiss}
                            className="w-full py-4 px-6 bg-gradient-to-r from-[#C0C0DD] to-[#AEAEAE] text-black font-medium rounded-2xl transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-[#C0C0DD]/30"
                            style={{ fontFamily: 'var(--font-primary)' }}
                        >
                            Got it
                        </button>
                    </div>

                    {/* Close button */}
                    <button
                        onClick={handleDismiss}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200 text-white/70 hover:text-white"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
            </div>
        </>
    );
};

export default AccountabilityBanner; 